import React, { useEffect, useState } from 'react'
import { FileText, Upload, Search, Trash2, MessageSquare, Brain, Zap, Clock, Link as LinkIcon, ExternalLink } from 'lucide-react'
import api from '../services/api'
import UploadResourceModal from '../components/resources/UploadResourceModal'
import CreateURLResourceModal from '../components/resources/CreateURLResourceModal'
import QueryResourceModal from '../components/resources/QueryResourceModal'
import ResourceDetailsModal from '../components/resources/ResourceDetailsModal'

interface Resource {
  id: string
  name: string
  description: string
  resource_type: string
  size_bytes: number
  total_tokens: number
  chunk_count: number
  query_count: number
  last_queried_at: string | null
  created_at: string
  tags: string[]
}

interface URLResource {
  id: string
  url: string
  name: string
  description: string | null
  usage_context: string
  usage_keywords: string[]
  response_template: string
  url_type: string
  use_count: number
  last_used_at: string | null
  created_at: string
  tags: string[]
  is_active: number
}

const Resources: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([])
  const [filteredResources, setFilteredResources] = useState<Resource[]>([])
  const [urlResources, setUrlResources] = useState<URLResource[]>([])
  const [filteredUrlResources, setFilteredUrlResources] = useState<URLResource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')

  // Tab state
  const [activeTab, setActiveTab] = useState<'documents' | 'urls'>('documents')

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showCreateURLModal, setShowCreateURLModal] = useState(false)
  const [showQueryModal, setShowQueryModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null)

  useEffect(() => {
    fetchResources()
    fetchUrlResources()
  }, [])

  useEffect(() => {
    filterResources()
  }, [resources, searchQuery, filterType])

  useEffect(() => {
    filterUrlResources()
  }, [urlResources, searchQuery, filterType])

  const fetchResources = async () => {
    try {
      setLoading(true)
      const response = await api.resources.list()
      setResources(response.resources)
      setError(null)
    } catch (err) {
      console.error('Error fetching resources:', err)
      setError('Error al cargar recursos')
    } finally {
      setLoading(false)
    }
  }

  const fetchUrlResources = async () => {
    try {
      const response = await api.urlResources.list()
      setUrlResources(response.resources)
    } catch (err) {
      console.error('Error fetching URL resources:', err)
    }
  }

  const filterResources = () => {
    let filtered = resources

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.resource_type === filterType)
    }

    setFilteredResources(filtered)
  }

  const filterUrlResources = () => {
    let filtered = urlResources

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.usage_context.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.usage_keywords.some(kw => kw.toLowerCase().includes(searchQuery.toLowerCase())) ||
        r.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    // Filter by URL type
    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.url_type === filterType)
    }

    setFilteredUrlResources(filtered)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este recurso? Esta acción no se puede deshacer.')) {
      return
    }

    try {
      await api.resources.delete(id)
      setResources(resources.filter(r => r.id !== id))
    } catch (err) {
      console.error('Error deleting resource:', err)
      alert('Error al eliminar el recurso')
    }
  }

  const handleUploadSuccess = () => {
    setShowUploadModal(false)
    fetchResources()
  }

  const handleCreateURLSuccess = () => {
    setShowCreateURLModal(false)
    fetchUrlResources()
  }

  const handleQueryClick = (resource: Resource) => {
    setSelectedResource(resource)
    setShowQueryModal(true)
  }

  const handleDetailsClick = (resource: Resource) => {
    setSelectedResource(resource)
    setShowDetailsModal(true)
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca'
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileText size={16} className="text-blue-500" />
      case 'manual':
        return <FileText size={16} className="text-purple-500" />
      case 'knowledge-base':
        return <Brain size={16} className="text-green-500" />
      case 'code':
        return <Zap size={16} className="text-yellow-500" />
      default:
        return <FileText size={16} className="text-gray-500" />
    }
  }

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      // Document types
      document: 'Documento',
      manual: 'Manual',
      'knowledge-base': 'Base de Conocimiento',
      code: 'Código',
      // URL types
      calendar: 'Calendario',
      image: 'Imagen',
      video: 'Video',
      profile: 'Perfil',
      other: 'Otro'
    }
    return labels[type] || type
  }

  const uniqueTypes = activeTab === 'documents'
    ? ['all', ...Array.from(new Set(resources.map(r => r.resource_type)))]
    : ['all', ...Array.from(new Set(urlResources.map(r => r.url_type)))]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recursos Inteligentes</h1>
          <p className="text-gray-600 mt-1">
            Documentos RAG y enlaces contextuales con IA
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateURLModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <LinkIcon size={20} />
            Crear Recurso URL
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload size={20} />
            Subir Documento
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'documents'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText size={18} />
              Documentos RAG
            </div>
          </button>
          <button
            onClick={() => setActiveTab('urls')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'urls'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <LinkIcon size={18} />
              Recursos URL
            </div>
          </button>
        </nav>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar recursos por nombre, descripción o tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {uniqueTypes.map(type => (
            <option key={type} value={type}>
              {type === 'all' ? 'Todos los tipos' : getTypeLabel(type)}
            </option>
          ))}
        </select>
      </div>

      {/* Stats */}
      {activeTab === 'documents' ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Recursos</p>
                <p className="text-2xl font-bold text-gray-900">{resources.length}</p>
              </div>
              <FileText className="text-blue-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tokens</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(resources.reduce((sum, r) => sum + r.total_tokens, 0) / 1000).toFixed(1)}K
                </p>
              </div>
              <Zap className="text-yellow-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Consultas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {resources.reduce((sum, r) => sum + r.query_count, 0)}
                </p>
              </div>
              <MessageSquare className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Chunks</p>
                <p className="text-2xl font-bold text-gray-900">
                  {resources.reduce((sum, r) => sum + r.chunk_count, 0)}
                </p>
              </div>
              <Brain className="text-purple-500" size={32} />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total URLs</p>
                <p className="text-2xl font-bold text-gray-900">{urlResources.length}</p>
              </div>
              <LinkIcon className="text-purple-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">URLs Activas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {urlResources.filter(r => r.is_active === 1).length}
                </p>
              </div>
              <Zap className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Usos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {urlResources.reduce((sum, r) => sum + r.use_count, 0)}
                </p>
              </div>
              <MessageSquare className="text-blue-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tipos Únicos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Array.from(new Set(urlResources.map(r => r.url_type))).length}
                </p>
              </div>
              <Brain className="text-purple-500" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Resources Grid */}
      {activeTab === 'documents' && filteredResources.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="mx-auto text-gray-400" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchQuery || filterType !== 'all' ? 'No se encontraron recursos' : 'No hay recursos'}
          </h3>
          <p className="mt-2 text-gray-600">
            {searchQuery || filterType !== 'all'
              ? 'Intenta ajustar tus filtros de búsqueda'
              : 'Comienza subiendo tu primer documento'}
          </p>
          {!searchQuery && filterType === 'all' && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Upload size={20} />
              Subir Recurso
            </button>
          )}
        </div>
      ) : null}

      {activeTab === 'documents' && filteredResources.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredResources.map((resource) => (
            <div
              key={resource.id}
              className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(resource.resource_type)}
                    <h3 className="font-semibold text-gray-900 truncate">{resource.name}</h3>
                  </div>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Description */}
                {resource.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {resource.description}
                  </p>
                )}

                {/* Tags */}
                {resource.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {resource.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {resource.tags.length > 3 && (
                      <span className="px-2 py-1 text-xs text-gray-500">
                        +{resource.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                  <div className="text-gray-600">
                    <span className="font-medium">{formatBytes(resource.size_bytes)}</span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">{(resource.total_tokens / 1000).toFixed(1)}K</span> tokens
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">{resource.chunk_count}</span> chunks
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">{resource.query_count}</span> consultas
                  </div>
                </div>

                {/* Last queried */}
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                  <Clock size={12} />
                  Última consulta: {formatDate(resource.last_queried_at)}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleQueryClick(resource)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <MessageSquare size={16} />
                    Consultar
                  </button>
                  <button
                    onClick={() => handleDetailsClick(resource)}
                    className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Ver Detalles
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* URL Resources Grid */}
      {activeTab === 'urls' && filteredUrlResources.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <LinkIcon className="mx-auto text-gray-400" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchQuery || filterType !== 'all' ? 'No se encontraron recursos URL' : 'No hay recursos URL'}
          </h3>
          <p className="mt-2 text-gray-600">
            {searchQuery || filterType !== 'all'
              ? 'Intenta ajustar tus filtros de búsqueda'
              : 'Comienza creando tu primer recurso URL'}
          </p>
          {!searchQuery && filterType === 'all' && (
            <button
              onClick={() => setShowCreateURLModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <LinkIcon size={20} />
              Crear Recurso URL
            </button>
          )}
        </div>
      ) : null}

      {activeTab === 'urls' && filteredUrlResources.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUrlResources.map((resource) => (
            <div
              key={resource.id}
              className="bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <LinkIcon size={16} className="text-purple-500" />
                    <h3 className="font-semibold text-gray-900 truncate">{resource.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {resource.is_active === 0 && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                        Inactivo
                      </span>
                    )}
                    <button
                      onClick={async () => {
                        if (confirm('¿Eliminar este recurso URL?')) {
                          await api.urlResources.delete(resource.id)
                          fetchUrlResources()
                        }
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* URL */}
                <div className="mb-3">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 truncate"
                  >
                    {resource.url}
                    <ExternalLink size={12} />
                  </a>
                </div>

                {/* Description */}
                {resource.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {resource.description}
                  </p>
                )}

                {/* Usage Context */}
                <div className="bg-blue-50 border border-blue-100 rounded p-2 mb-3">
                  <p className="text-xs text-gray-700 line-clamp-2">
                    <span className="font-medium">Contexto: </span>
                    {resource.usage_context}
                  </p>
                </div>

                {/* Keywords */}
                {resource.usage_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {resource.usage_keywords.slice(0, 3).map((kw, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded"
                      >
                        {kw}
                      </span>
                    ))}
                    {resource.usage_keywords.length > 3 && (
                      <span className="px-2 py-1 text-xs text-gray-500">
                        +{resource.usage_keywords.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Tags */}
                {resource.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {resource.tags.slice(0, 2).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {resource.tags.length > 2 && (
                      <span className="px-2 py-1 text-xs text-gray-500">
                        +{resource.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                  <div className="text-gray-600">
                    <span className="font-medium">{resource.use_count}</span> usos
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">{getTypeLabel(resource.url_type)}</span>
                  </div>
                </div>

                {/* Last used */}
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                  <Clock size={12} />
                  Último uso: {formatDate(resource.last_used_at)}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    <ExternalLink size={16} />
                    Abrir URL
                  </a>
                  <button
                    onClick={async () => {
                      await api.urlResources.toggle(resource.id)
                      fetchUrlResources()
                    }}
                    className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    {resource.is_active === 1 ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showUploadModal && (
        <UploadResourceModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showCreateURLModal && (
        <CreateURLResourceModal
          onClose={() => setShowCreateURLModal(false)}
          onSuccess={handleCreateURLSuccess}
        />
      )}

      {showQueryModal && selectedResource && (
        <QueryResourceModal
          resource={selectedResource}
          onClose={() => {
            setShowQueryModal(false)
            setSelectedResource(null)
          }}
          onQuerySuccess={() => fetchResources()}
        />
      )}

      {showDetailsModal && selectedResource && (
        <ResourceDetailsModal
          resourceId={selectedResource.id}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedResource(null)
          }}
        />
      )}
    </div>
  )
}

export default Resources
